import Link from 'next/link';
import styled from 'styled-components';

type BeekLogoProps = {
  type?: 'black' | 'white' | 'color';
  marginTop?: string;
  marginBottom?: string;
  marginRight?: string;
  marginLeft?: string;
  variant?: 'normal' | 'small';
};

const defaultProps = {
  type: 'color',
  marginTop: '0rem',
  marginBottom: '0rem',
  marginRight: '0rem',
  marginLeft: '0rem',
  variant: 'normal',
};

const getColorsByType = (type: string): [string, string] => {
  let iconColor = 'var(--beek-blue)';
  let textColor = 'var(--gray-100)';

  switch (type) {
    case 'black':
      iconColor = 'var(--gray-100)';
      textColor = 'var(--gray-100)';
      break;
    case 'white':
      iconColor = 'var(--white)';
      textColor = 'var(--white)';
      break;
    default:
      iconColor = 'var(--beek-blue)';
      textColor = 'var(--gray-100)';
      break;
  }

  return [iconColor, textColor];
};

const SvgContainerNormal = styled.svg`
  width: 5.625rem;
  height: 2rem;
  fill: none;
  @media ${({ theme }): string => theme.devices.laptop} {
    fill: none;
    width: 7.5rem;
    height: 2rem;
  }
`;

const SvgContainerSmall = styled.svg`
  width: auto;
  height: auto;
`;

const AnchorDesktop = styled.a<BeekLogoProps>`
  ${(props): string | undefined =>
    props.variant === 'small' ? 'display: none' : 'display: inline'};
  margin-bottom: ${({ marginBottom }): string | undefined => marginBottom};

  @media ${({ theme }): string => theme.devices.laptop} {
    display: inline;
    margin-top: ${({ marginTop }): string | undefined => marginTop};
    margin-bottom: ${({ marginBottom }): string | undefined => marginBottom};
    margin-right: ${({ marginRight }): string | undefined => marginRight};
    margin-left: ${({ marginLeft }): string | undefined => marginLeft};
  }
`;

const AnchorMobile = styled.a<BeekLogoProps>`
  ${(props): string | undefined =>
    props.variant === 'small' ? 'display: inline' : 'display: none'};
  margin-top: ${({ marginTop }): string | undefined => marginTop};
  margin-bottom: ${({ marginBottom }): string | undefined => marginBottom};
  margin-right: ${({ marginRight }): string | undefined => marginRight};
  margin-left: ${({ marginLeft }): string | undefined => marginLeft};
  @media ${({ theme }): string => theme.devices.laptop} {
    display: none;
  }
`;

const BeekLogo = (props: BeekLogoProps): JSX.Element => {
  const { type } = props;
  let iconColor;
  let textColor;
  if (type !== undefined) [iconColor, textColor] = getColorsByType(type);
  return (
    <>
      <Link href="/" passHref>
        <AnchorDesktop
          rel="noreferrer"
          {...props}
          data-testid="beekLogo-normal"
        >
          <SvgContainerNormal
            viewBox="0 0 225 64"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M28 15.345L0 2v40.645l18.53 8.964-6.795 9.678 13.794-6.621L33.044 62l4.838-11.41L56 42.646V2L28 15.345zm21 23.022L21.309 51.609l3.088-5.195L7 38.264V12.493l21 9.78 21-9.78v25.875z"
              fill={iconColor}
            />
            <path
              d="M111.857 38.265c0 10.085-6.112 16.91-15.585 16.91-4.686 0-8.354-1.834-10.798-5.195v4.89H76V8.722h9.576v17.623c2.343-3.361 6.01-5.195 10.594-5.195 9.27-.102 15.687 6.825 15.687 17.114zm-9.779-.306c0-5.297-3.362-9.168-8.251-9.168-4.89 0-8.353 3.77-8.353 9.168 0 5.501 3.463 9.27 8.353 9.27s8.251-3.769 8.251-9.27zM148.937 41.015h-24.041c1.121 4.177 4.381 6.724 8.659 6.724 3.158 0 6.112-1.223 8.455-3.566l4.992 5.094c-3.362 3.769-8.252 5.908-14.262 5.908-10.696 0-17.419-6.825-17.419-17.012 0-10.289 7.029-17.114 17.216-17.114 11.714 0 17.215 7.742 16.4 19.966zm-8.964-5.602c-.102-4.38-2.954-7.233-7.436-7.233-4.177 0-7.131 2.852-7.844 7.233h15.28zM169.514 21.05c10.187 0 17.216 6.824 17.216 17.113 0 10.085-6.723 17.012-17.419 17.012-6.01 0-10.9-2.14-14.262-5.908l4.992-5.094c2.343 2.343 5.297 3.566 8.455 3.566 4.278 0 7.538-2.445 8.659-6.724h-23.939c-.917-12.224 4.584-19.966 16.298-19.966zm7.844 14.363c-.713-4.38-3.565-7.233-7.844-7.233-4.38 0-7.232 2.852-7.436 7.233h15.28zM204.659 41.219l-4.279 4.584v9.066h-9.575V8.723h9.575v25.365l12.224-12.631h10.9L211.28 34.292l12.835 20.475h-11.307l-8.149-13.548z"
              fill={textColor}
            />
          </SvgContainerNormal>
        </AnchorDesktop>
      </Link>
      <Link href="/" passHref>
        <AnchorMobile rel="noreferrer" {...props} data-testid="beekLogo-small">
          <SvgContainerSmall
            width="32"
            height="32"
            viewBox="0 0 32 32"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M16.4601 8.58883L4 2.66667V20.725L12.281 24.6969L9.25653 28.9997L15.4164 26.0765L18.7709 29.3333L20.9155 24.2556L28.9999 20.6147V2.66667L16.4601 8.58883ZM25.8767 18.8408L13.5023 24.6882L14.8768 22.3713L7.12239 18.786V7.31179L16.4991 11.6694L25.8758 7.31179V18.8408H25.8767Z"
              fill={iconColor}
            />
          </SvgContainerSmall>
        </AnchorMobile>
      </Link>
    </>
  );
};

BeekLogo.defaultProps = defaultProps;

export default BeekLogo;
